import type { SegmentedOption } from "@norkron/web-segmented-control";
import type {
  AnchorState,
  Ticket,
  TicketStatus,
  TicketType,
} from "../api/types";

/**
 * TICKETS QUEUE vocabulary + pure helpers (design screens §6, ia-and-flows §5).
 * The type/status vocabulary is elaborate-owned (component-map §3.2 —
 * `TicketFilters` type/status vocab). Pure + framework-free so it unit-tests
 * without a DOM; the icon glyphs (React nodes) live in the screen, this file
 * owns only labels, filter segments, anchor-state semantics, and age formatting.
 */

// ── comment types ──────────────────────────────────────────────────────────
export const TICKET_TYPES: readonly TicketType[] = [
  "request-for-change",
  "suggestion",
  "comment",
] as const;

const TYPE_LABEL: Record<TicketType, string> = {
  "request-for-change": "Request for change",
  suggestion: "Suggestion",
  comment: "Comment",
};

/** Short label used in the filter pill (design §6: `rfc · suggestion · comment`). */
const TYPE_SHORT: Record<TicketType, string> = {
  "request-for-change": "RFC",
  suggestion: "Suggestion",
  comment: "Comment",
};

export function typeLabel(t: TicketType): string {
  return TYPE_LABEL[t] ?? t;
}

// ── statuses ────────────────────────────────────────────────────────────────
export const TICKET_STATUSES: readonly TicketStatus[] = [
  "open",
  "resolved",
  "acted_on",
  "awaiting_re_review",
  "pinned",
  "orphaned",
] as const;

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  resolved: "Resolved",
  acted_on: "Acted on",
  awaiting_re_review: "Re-review",
  pinned: "Pinned",
  orphaned: "Orphaned",
};

export function statusLabel(s: TicketStatus): string {
  return STATUS_LABEL[s] ?? s;
}

// ── anchor state (design §6 ANCHOR column) ──────────────────────────────────
//
// Both tables below are EXHAUSTIVE `Record`s over `AnchorState`. That is the
// point: a new server state cannot reach these screens without a compile error
// forcing someone to say what it is called and whether it is surfaced. They used
// to cover three values while the server emitted five, and the two it did not
// cover took the unlabelled, unflagged path.
const ANCHOR_LABEL: Record<AnchorState, string> = {
  exact: "Anchored",
  shifted: "Shifted",
  repaired: "Repaired",
  orphaned: "Orphaned",
  // NOT "Moved", NOT "Lost": this comment's quote never attached to the document
  // in the first place, so nothing about it moved and nothing was lost.
  unanchored: "Not anchored",
};

export function anchorStateLabel(a: AnchorState): string {
  return ANCHOR_LABEL[a] ?? a;
}

/**
 * Does this anchor state earn a badge?
 *
 * ⛔ `exact` is the ONLY state that earns silence, because it is the only one
 * that means "this quote is still exactly where it was written". The callers
 * (`Tickets.tsx`, `KanbanCard.tsx`) treat the false branch as a positive claim —
 * the tickets grid literally renders the word "Anchored" there — so returning
 * false is not "no opinion", it is the assertion that the anchor is good.
 *
 * `unanchored` is therefore flagged, and the argument is not close: a comment
 * that never anchored is strictly LESS trustworthy than one that shifted or was
 * repaired, both of which are flagged. It cannot be the quieter of the two. It
 * is not "unflagged-normal" — its quote is in no section of the document, the
 * jump-to-anchor affordance has nowhere to go, and the reviewer needs to know
 * that before treating the quote as a citation. This is the same defect the
 * server-side change killed one layer down: reporting confidence for an anchor
 * that does not exist.
 */
const ANCHOR_FLAGGED: Record<AnchorState, boolean> = {
  exact: false,
  shifted: true,
  repaired: true,
  orphaned: true,
  unanchored: true,
};

export function anchorIsFlagged(a: AnchorState): boolean {
  return ANCHOR_FLAGGED[a] ?? true;
}

// ── filter segments (feed the SegmentedControl; "all" clears the filter) ────
//
// Typed on the LIB's own option shape (`@norkron/web-segmented-control`), not on
// a local `FilterSegment` mirror. The mirror existed only because the pill bar
// itself was hand-rolled; with the real primitive composed, a second declaration
// of `{ value, label }` is a drift surface — it silently forbids the lib's
// `count` / `icon` / `disabled` / `tooltip` fields at exactly the call sites
// that would want them.
export const TYPE_SEGMENTS: SegmentedOption<TicketType | "all">[] = [
  { value: "all", label: "All" },
  ...TICKET_TYPES.map((t) => ({ value: t, label: TYPE_SHORT[t] })),
];

export const STATUS_SEGMENTS: SegmentedOption<TicketStatus | "all">[] = [
  { value: "all", label: "All" },
  ...TICKET_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
];

// ── client-side filter (mirrors the server `?type=&status=`; used by the mock
//    adapter + as a defensive client filter, and unit-tested here) ───────────
export function filterTickets(
  tickets: Ticket[],
  opts: { type?: TicketType | "all"; status?: TicketStatus | "all" },
): Ticket[] {
  return tickets.filter((t) => {
    if (opts.type && opts.type !== "all" && t.type !== opts.type) return false;
    if (opts.status && opts.status !== "all" && t.status !== opts.status)
      return false;
    return true;
  });
}

// ── relative age ("1d", "3h", "just now") ───────────────────────────────────
export function formatAge(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.floor((now.getTime() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
}
